import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import './ShareLinkButton.css'

const VIEWPORT_PAD = 8
const POPOVER_GAP = 6

/** Close any other open share popover when a new one opens. */
let closeOtherSharePopover = null

function requestExclusiveSharePopover(closeSelf) {
  if (closeOtherSharePopover && closeOtherSharePopover !== closeSelf) {
    closeOtherSharePopover()
  }
  closeOtherSharePopover = closeSelf
}

function releaseExclusiveSharePopover(closeSelf) {
  if (closeOtherSharePopover === closeSelf) {
    closeOtherSharePopover = null
  }
}

function ShareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <circle cx="12" cy="3.5" r="1.75" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12.5" r="1.75" fill="currentColor" stroke="none" />
      <circle cx="4" cy="8" r="1.75" fill="currentColor" stroke="none" />
      <path d="M5.65 7.15l4.7-2.45M5.65 8.85l4.7 2.45" strokeLinecap="round" />
    </svg>
  )
}

async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  }
}

function clampPopoverPosition(triggerRect, popoverRect) {
  const viewportW = window.innerWidth
  const viewportH = window.innerHeight

  let top = triggerRect.bottom + POPOVER_GAP
  if (top + popoverRect.height > viewportH - VIEWPORT_PAD) {
    const above = triggerRect.top - popoverRect.height - POPOVER_GAP
    if (above >= VIEWPORT_PAD) {
      top = above
    }
  }

  let left = triggerRect.right - popoverRect.width
  if (left < VIEWPORT_PAD) left = VIEWPORT_PAD
  if (left + popoverRect.width > viewportW - VIEWPORT_PAD) {
    left = Math.max(VIEWPORT_PAD, viewportW - VIEWPORT_PAD - popoverRect.width)
  }

  top = Math.min(Math.max(top, VIEWPORT_PAD), viewportH - VIEWPORT_PAD - popoverRect.height)

  return { top, left }
}

function ShareLinkButton({ path, className = '', variant = 'default' }) {
  const [isOpen, setIsOpen] = useState(false)
  const [copyState, setCopyState] = useState('idle')
  const [popoverStyle, setPopoverStyle] = useState(null)
  const wrapRef = useRef(null)
  const buttonRef = useRef(null)
  const popoverRef = useRef(null)
  const copyActionRef = useRef(null)
  const closeAfterCopyTimerRef = useRef(null)
  const popoverIdRef = useRef(`share-link-popover-${Math.random().toString(36).slice(2, 9)}`)

  const closePopover = useCallback(() => {
    setIsOpen(false)
    setCopyState('idle')
  }, [])

  useEffect(() => {
    return () => {
      if (closeAfterCopyTimerRef.current) window.clearTimeout(closeAfterCopyTimerRef.current)
      releaseExclusiveSharePopover(closePopover)
    }
  }, [closePopover])

  const updatePopoverPosition = useCallback(() => {
    const trigger = buttonRef.current
    const popover = popoverRef.current
    if (!trigger || !popover) return

    const next = clampPopoverPosition(trigger.getBoundingClientRect(), popover.getBoundingClientRect())
    setPopoverStyle((prev) =>
      prev && prev.top === next.top && prev.left === next.left ? prev : next
    )
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) {
      setPopoverStyle(null)
      releaseExclusiveSharePopover(closePopover)
      return
    }
    requestExclusiveSharePopover(closePopover)
    updatePopoverPosition()
    copyActionRef.current?.focus()
  }, [isOpen, copyState, updatePopoverPosition, closePopover])

  useEffect(() => {
    if (!isOpen) return

    function handlePointerDownOutside(event) {
      const target = event.target
      if (wrapRef.current?.contains(target) || popoverRef.current?.contains(target)) return
      closePopover()
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        closePopover()
        buttonRef.current?.focus()
      }
    }

    function handleViewportChange() {
      updatePopoverPosition()
    }

    document.addEventListener('pointerdown', handlePointerDownOutside)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [isOpen, updatePopoverPosition, closePopover])

  const stopAnchorNavigation = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleToggle = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      setIsOpen((open) => {
        if (open) setCopyState('idle')
        return !open
      })
    },
    []
  )

  const handleCopyLink = useCallback(
    async (e) => {
      e.preventDefault()
      e.stopPropagation()
      let url
      try {
        url = new URL(path, window.location.origin).href
      } catch {
        setCopyState('failed')
        return
      }
      const ok = await copyTextToClipboard(url)
      if (ok) {
        setCopyState('copied')
        if (closeAfterCopyTimerRef.current) window.clearTimeout(closeAfterCopyTimerRef.current)
        closeAfterCopyTimerRef.current = window.setTimeout(() => {
          closePopover()
          buttonRef.current?.focus()
        }, 900)
      } else {
        setCopyState('failed')
      }
    },
    [path, closePopover]
  )

  if (!path || typeof path !== 'string') {
    return null
  }

  let popoverLabel = 'Copy link'
  if (copyState === 'copied') popoverLabel = 'Link copied'
  else if (copyState === 'failed') popoverLabel = 'Could not copy link'

  return (
    <div
      className={`share-link-button-wrap share-link-button-wrap--${variant} ${isOpen ? 'share-link-button-wrap--open' : ''} ${className}`.trim()}
      ref={wrapRef}
    >
      <button
        ref={buttonRef}
        type="button"
        className={`share-link-button share-link-button--${variant} ${isOpen ? 'share-link-button--open' : ''}`}
        onClick={handleToggle}
        onMouseDown={stopAnchorNavigation}
        title="Share link"
        aria-label="Share link"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={isOpen ? popoverIdRef.current : undefined}
      >
        <ShareIcon />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            id={popoverIdRef.current}
            className="share-link-popover"
            role="menu"
            aria-label="Share repository link"
            style={
              popoverStyle
                ? { top: popoverStyle.top, left: popoverStyle.left, visibility: 'visible' }
                : { top: 0, left: 0, visibility: 'hidden' }
            }
          >
            <button
              ref={copyActionRef}
              type="button"
              role="menuitem"
              className={`share-link-popover-action share-link-popover-action--${copyState}`}
              onClick={handleCopyLink}
              onMouseDown={stopAnchorNavigation}
              disabled={copyState === 'copied'}
            >
              {popoverLabel}
            </button>
          </div>,
          document.body
        )}
    </div>
  )
}

export default ShareLinkButton
